import TextField from '@mui/material/TextField/TextField';
import Button from '@mui/material/Button/Button';
import Box from '@mui/material/Box/Box';
import { composeStore } from 'utils/composeStore';
import NewReferenceCheck, {
	useReferenceStore,
} from 'components/NewReferenceCheck';
import { useBillingInfo } from './hooks/hooks';
import { newRequestV2Schema } from './schema/schema';
import { toast } from 'react-toastify';
import { createCandidate, updateLifeCycle } from 'apis/user.api';
import { detectBrowser, getDate, useIP, useQuestionnaires } from 'Common';

import { useEffect, useRef, useState } from 'react';
import { ZodError } from 'zod';
import Loading from 'components/Loading';
import { FuturePaymentBox } from './FuturePaymentBox';

export const useChecksStore = composeStore<{
	selectedChecks: Array<string>;
}>({
	name: 'selectedChecks',
	initialState: { selectedChecks: ['reference'] },
	storage: null,
});

export default function NewRequestV2() {
	const { data: billingData, isLoading: billingLoading } = useBillingInfo();
	const [shouldReset, setShouldReset] = useState(false);
	const emailRef = useRef<HTMLInputElement>(null);
	useQuestionnaires();
	const referenceStore = useReferenceStore();
	const { setStore, selectedChecks } = useChecksStore();
	const { data: ipAddress } = useIP();
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		emailRef.current?.focus();
	}, []);

	if (status === 'pending' || status === 'error') {
		return <Loading />;
	}

	// Function to validate reference check form
	const validateReferenceForm = () => {
		// Check if the reference check is selected
		if (!selectedChecks.includes('reference')) {
			return true; // Skip validation if reference check is not selected
		}

		const { firstName, lastName, phone, role, questionnaire } = referenceStore;

		// Validate required fields
		let isValid = true;
		let errorMessage = '';

		// Validate first name
		if (!firstName || firstName.trim() === '') {
			document.getElementById('firstNameError')!.style.display = 'block';
			document.getElementById('first-name-field')!.classList.add('error-field');
			isValid = false;
			errorMessage = 'First Name is required';
		}

		// Validate name fields don't contain numbers
		const namePattern = /[0-9]/;
		if (namePattern.test(firstName + lastName)) {
			document.getElementById('numberError')!.style.display = 'block';
			document.getElementById('first-name-field')!.classList.add('error-field');
			isValid = false;
			errorMessage = 'Names should not contain numbers';
		}

		// Validate last name
		if (!lastName || lastName.trim() === '') {
			document.getElementById('lastNameError')!.style.display = 'block';
			document.getElementById('last-name-field')!.classList.add('error-field');
			isValid = false;
			errorMessage = errorMessage || 'Last Name is required';
		}

		// Validate phone
		if (!phone || phone.trim().length <= 0) {
			document.getElementById('phoneError')!.style.display = 'block';
			document.getElementById('phone-field')!.classList.add('error-field');
			isValid = false;
			errorMessage = errorMessage || 'Phone number is required';
		}

		// Check for fake phone numbers
		const fakePatternsRegex = /[(][0-9]{3}[)][" "][0-9]{3}[-][0-9]{4}/;
		const repeatingDigits =
			/[(]([0-9])\1{2}[)][" "]([0-9])\2{2}[-]([0-9])\3{3}/;
		if (phone && repeatingDigits.test(phone)) {
			document.getElementById('phoneErrorInvalid')!.style.display = 'block';
			document.getElementById('phone-field')!.classList.add('error-field');
			isValid = false;
			errorMessage = errorMessage || 'Phone number is invalid';
		}

		// Validate job title
		if (!role || role.trim() === '' || role.trim().length < 4) {
			document.getElementById('roleError')!.style.display = 'block';
			document.getElementById('job-title-field')!.classList.add('error-field');
			isValid = false;
			errorMessage =
				errorMessage || 'Job title should be at least 4 characters';
		}

		// Validate questionnaire
		if (!questionnaire) {
			document.getElementById('questionnaireError')!.style.display = 'block';
			document
				.getElementById('select-questionnaire')!
				.classList.add('error-field');
			isValid = false;
			errorMessage = errorMessage || 'Questionnaire is required';
		}

		if (!isValid && errorMessage) {
			toast.error(errorMessage);
		}

		return isValid;
	};

	const handleSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
		e.preventDefault();

		if (!billingData?.data.Customer || isLoading) return;

		// Validate the reference form first
		if (!validateReferenceForm()) {
			return; // Stop submission if validation fails
		}

		const obj = Object.fromEntries(new FormData(e.currentTarget).entries());
		try {
			setIsLoading(true);
			//Filter the ContactsList UUIDS which are cc on...
			const on_cc_contactsUUID = referenceStore.contactsList
				.filter(contact => contact.isccresults)
				.map(contact => contact.uuid);

			const { email } = newRequestV2Schema.parse(obj);
			if (selectedChecks.includes('reference') && referenceStore.role) {
				const resp = await createCandidate({
					email,
					recruiter: referenceStore.recruiter,
					firstName: referenceStore.firstName.trim(),
					lastName: referenceStore.lastName.trim(),
					phone: referenceStore.phone,
					role: referenceStore.role,
					questionnaire: referenceStore.questionnaire,
					requestDate: referenceStore.requestDate,
					response: referenceStore.response,
					recruiterName: referenceStore.recruiter,
					recruiterTZ: Intl.DateTimeFormat().resolvedOptions().timeZone,
					min_reference: referenceStore.min_reference,
					is_sms_allow: referenceStore.is_sms_allow,
					cc_email: on_cc_contactsUUID,
				});

				const body = {
					uuid: resp.data.uuid,
					name: resp.data.recruiterName,
					userType: 'recruiter',
					date: getDate(),
					action: 'Sent request to Candidate',
					osBrowser: detectBrowser(),
					ipAddress: !!ipAddress ? ipAddress : null,
					locationISP: localStorage.getItem('cityName'),
					refereeUUID: resp.data.uuid,
				};
				await updateLifeCycle(body);
				referenceStore.setStore({
					recruiter: '',
					firstName: '',
					lastName: '',
					phone: '',
					role: '',
					questionnaire: '',
					requestDate: '',
					response: '',
					recruiterName: '',
					recruiterTZ: '',
					min_reference: 2,
					is_sms_allow: false,
					contactsList: [],
				});
				setShouldReset(true);
				toast.success('Reference submitted successfully!');
				setTimeout(() => {
					setShouldReset(false);
				}, 200);
			}

			if (!selectedChecks.filter(v => v !== 'reference').length) {
				setStore({ selectedChecks: [] });
				if (emailRef.current) {
					emailRef.current.value = '';
				}
				return;
			}
		} catch (e: any) {
			if (e instanceof ZodError) {
				toast.error(e.issues.map(v => v.message).join('\n'));
				return;
			}
			toast.error(e.message! ?? 'Something went wrong!');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Box>
			{!billingLoading && !billingData?.data.Customer && <FuturePaymentBox />}
			<form onSubmit={handleSubmit}>
				<TextField
					type='email'
					variant='outlined'
					label='Email'
					required
					fullWidth
					autoComplete='email'
					helperText="Enter the candidate's email address"
					name='email'
					inputRef={emailRef}
				/>
				<br />
				<br />

				<NewReferenceCheck shouldReset={shouldReset} />
				<Box className='mx-auto my-4 text-center'>
					<Button
						type='submit'
						variant='contained'
						color='secondary'
						disabled={isLoading || !billingData?.data.Customer}
						loading={isLoading}
					>
						{isLoading ? 'Sending...' : 'Send Request'}
					</Button>
				</Box>
			</form>
		</Box>
	);
}
